import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import AgentSelect from "./AgentSelect";
import DayOfWeekField, { DEFAULT_DAYS, scheduleText, defaultTimezone } from "./DayOfWeekField";
import SequencePickerModal from "./SequencePickerModal";

const CreateCompanyProspector = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [savedSearchId, setSavedSearchId] = useState("");
  const [titles, setTitles] = useState("");
  const [perCompany, setPerCompany] = useState(5);
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [seqModalOpen, setSeqModalOpen] = useState(false);
  const [runWhen, setRunWhen] = useState("DAILY");
  const [time, setTime] = useState("08:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [dailyCap, setDailyCap] = useState(25);
  const [notifyMode, setNotifyMode] = useState("DAILY_DIGEST");
  const [saving, setSaving] = useState(false);
  const timezone = defaultTimezone();

  useEffect(() => {
    agentsData.fetchSavedSearches();
  }, []);

  const savedSearch = (agentsData.savedSearches || []).find(s => s.id === savedSearchId);
  const canSubmit = !!savedSearchId && !!sequenceId;

  const buildPayload = () => ({
    name: name.trim() || "Company prospector",
    savedSearchId: savedSearchId || undefined,
    titles: titles.trim() || undefined,
    perCompany: Number(perCompany),
    sequenceId,
    dailyCap: Number(dailyCap),
    notifyMode,
    timezone,
    ...(runWhen === "MANUAL" ? {} : { frequency: runWhen, time, ...(runWhen === "WEEKLY" ? { days } : {}) })
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createCompanyProspector(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  // Open this template's steps in the canvas to tweak before creating.
  const customize = async () => {
    setSaving(true);
    const draft = await agentsData.composeTemplate("company-prospector", buildPayload());
    setSaving(false);
    if (draft) { agentsData.stageDraft(draft); history.push("/agents/new/custom"); }
  };

  const step = node => (<li><span className="ck">✓</span><span>{node}</span></li>);
  const runText = runWhen === "MANUAL" ? "when you run it" : scheduleText(runWhen, time, days, timezone);

  return (
    <div className="ag-page">
      <div className="ag-page__head">
        <div>
          <button className="ag-link" onClick={() => history.push("/agents/new")}>← Back</button>
          <h1 className="ag-page__title">Hire · Companies → people</h1>
          <p className="ag-page__sub">Find companies with a List Builder saved search, get the people who work there, enrich them, and enroll them.</p>
        </div>
      </div>

      <div className="ag-setup">
        <div className="ag-card-panel">
          <div className="ag-fsec">Which companies</div>
          <label className="ag-field">
            <span className="ag-field__label">Saved search<b className="ag-req">required</b></span>
            <AgentSelect searchable placeholder="Select a List Builder saved search…" value={savedSearchId}
              onChange={setSavedSearchId}
              options={(agentsData.savedSearches || []).map(ss => ({ value: ss.id, label: ss.label }))} />
            <span className="ag-hint">
              Its company filters (industry, size, location, keywords, technologies, funding, similar companies…) decide which companies are found.
              Build and save one in List Builder's Companies tab.
            </span>
          </label>
          <div className="ag-fsec">Which people</div>
          <label className="ag-field">
            <span className="ag-field__label">Job titles</span>
            <input className="ag-input" value={titles} placeholder="e.g. VP Sales, Head of Marketing, Founder" onChange={e => setTitles(e.target.value)} />
            <span className="ag-hint">Comma-separated. Who to find at each company — leave blank for anyone.</span>
          </label>
          <label className="ag-field">
            <span className="ag-field__label">People per company</span>
            <input className="ag-input" type="number" min="1" max="25" value={perCompany} onChange={e => setPerCompany(e.target.value)} />
          </label>
          <label className="ag-field">
            <span className="ag-field__label">Agent name</span>
            <input className="ag-input" value={name} placeholder="Company prospector" onChange={e => setName(e.target.value)} />
          </label>

          <div className="ag-fsec">What happens next</div>
          <label className="ag-field">
            <span className="ag-field__label">Enroll into sequence<b className="ag-req">required</b></span>
            <button type="button" className="ag-select__btn" onClick={() => setSeqModalOpen(true)}>
              <span className={sequenceName ? "" : "ag-select__ph"}>{sequenceName || "Select a sequence…"}</span>
              <svg className="ag-select__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            <span className="ag-hint">People are deduped and undeliverables dropped first; email sequences enrich the email before enrolling.</span>
          </label>

          <div className="ag-fsec">Schedule</div>
          <div className="ag-field-row">
            <label className="ag-field"><span className="ag-field__label">Run</span>
              <AgentSelect value={runWhen} onChange={setRunWhen} options={[
                { value: "DAILY", label: "Every day" },
                { value: "WEEKDAYS", label: "Weekdays (Mon–Fri)" },
                { value: "WEEKLY", label: "Specific days" },
                { value: "MANUAL", label: "Only when I run it" }
              ]} />
            </label>
            {runWhen === "WEEKLY" && <DayOfWeekField value={days} onChange={setDays} />}
            {runWhen !== "MANUAL" && (
              <label className="ag-field"><span className="ag-field__label">At</span>
                <input className="ag-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
              </label>
            )}
            <label className="ag-field"><span className="ag-field__label">Contacts / run</span>
              <input className="ag-input" type="number" min="1" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
            </label>
          </div>

          <div className="ag-fsec">Notifications</div>
          <label className="ag-field">
            <span className="ag-field__label">Notify me</span>
            <AgentSelect value={notifyMode} onChange={setNotifyMode} options={[
              { value: "DAILY_DIGEST", label: "Daily digest — one summary a day" },
              { value: "ON_FAILURE", label: "Only when a run fails" },
              { value: "OFF", label: "Off — don't email me" }
            ]} />
          </label>
        </div>

        <div className="ag-rail">
          <div className="ag-summary">
            <h4>⚡ What this agent does</h4>
            <ul className="ag-slist">
              {step(<span>Finds companies with {savedSearch ? <b>{savedSearch.label}</b> : "your saved search"}, {runText}</span>)}
              {step(<span>Gets up to <b>{perCompany}</b> {titles.trim() ? <b>{titles.trim()}</b> : "people"} at each company</span>)}
              {step(<span>Skips already-processed / DNC / undeliverable, enriches emails</span>)}
              {sequenceId && step(<span>Enrolls into <b>{sequenceName}</b></span>)}
              {notifyMode !== "OFF" && step(<span>{notifyMode === "DAILY_DIGEST" ? "Emails you a daily digest" : "Emails you when a run fails"}</span>)}
            </ul>
          </div>
          <button className="ag-btn ag-btn--primary" style={{ justifyContent: "center" }} disabled={!canSubmit || saving} onClick={submit}>
            {saving ? "Creating…" : "Create agent →"}
          </button>
          <button className="ag-btn" style={{ justifyContent: "center" }} disabled={saving} onClick={customize}>Customize steps →</button>
          <button className="ag-btn" style={{ justifyContent: "center" }} onClick={() => history.push("/agents")}>Cancel</button>
          {!canSubmit && <p className="ag-hint" style={{ textAlign: "center" }}>Pick a saved search and a sequence to create — or “Customize steps” to edit the flow first.</p>}
        </div>
      </div>

      <SequencePickerModal
        open={seqModalOpen}
        currentId={sequenceId}
        onSelect={s => { setSequenceId(s.id); setSequenceName(s.name); }}
        onClose={() => setSeqModalOpen(false)}
      />
    </div>
  );
});

export default CreateCompanyProspector;
